"use client";

import { Analytics } from "@vercel/analytics/next";

/**
 * Vercel Analytics with the secret bits of the URL stripped out before anything
 * leaves the browser.
 *
 * THIS IS NOT A TIDINESS FIX. Vercel Analytics records the RESOLVED url, not the
 * Next.js route pattern, so `<Analytics />` on its own posted every capability
 * link a user opened straight into the analytics dashboard. Found on
 * 2026-08-24, already live, with real values sitting in the store:
 *
 *   /reset-password/8J9SPolWpRfhyMokDInOXkHSvNMi6Isj4cQxHO-Gy-M
 *   /sign/ena3xvpVUcA6j1
 *   /feedback/1Bo_jhmAVodNxBDVNiXwvRbT039nC3_lmaX753Jaq2A
 *   /questionnaire/UhG2Q9LkLfo47IZ_HuyhFw-U70qlgI0pS5sG_r-1bGs
 *
 * Those are not identifiers, they are KEYS. The token IS the authentication on
 * those routes, which is the whole point of a link you can email to somebody who
 * has no account. Anyone holding the reset one can take over that account;
 * anyone holding the sign one can sign a contract as that client.
 *
 * So the rule here is the same one that applies to logs: a URL that grants
 * access is a credential, and a credential never goes anywhere it is not needed.
 */

/**
 * Routes whose segment after the prefix is ALWAYS a credential.
 *
 * Belt and braces, not the actual defence. The defence is `looksLikeIdentifier`
 * below, which redacts anything secret-shaped whether or not somebody remembered
 * to add the route here. The list stays because an explicit rule is easier to
 * read than a heuristic, and it catches a token short enough for the shape test
 * to wave through.
 */
const CREDENTIAL_ROUTES = [
  "reset-password",
  "sign",
  "invoice",
  "feedback",
  "questionnaire",
  "review",
  "testimonial/submit",
  "case-study/submit",
  "join",
  "r",
  "a",
] as const;

/**
 * Routes whose segment after the prefix is ALWAYS a record id.
 *
 * Also belt and braces. `looksLikeIdentifier` catches a real cuid on its own
 * (they are 25 characters), but not a short id, and there is no reason for the
 * analytics store to hold a list of which CRM records were opened regardless of
 * how short the id happens to be. Caught by a test on 2026-08-24 when the
 * rewrite dropped this list and `/closers/cmabc123` started coming through
 * intact.
 */
const RECORD_ROUTES = ["leads", "closers", "projects", "case-studies"] as const;

/**
 * Does this path segment look like an id or a token rather than a page name?
 *
 * THIS IS THE PART THAT MATTERS, and it is a rewrite of the first version.
 *
 * That version only redacted a hand-written list of routes, which fails the
 * wrong way round: an emailed-link route added in six months would leak every
 * token until somebody remembered to come back and edit this file. A comment
 * saying "remember to update this" is not a control, it is a wish.
 *
 * So the default is now REDACT, and a segment has to look like a real page name
 * to survive. A new route, a new token format, or code written by somebody who
 * has never opened this file are all covered without anyone having to know.
 *
 * The order below is deliberate: a UUID is tested first because it is lower case
 * with hyphens and would otherwise be mistaken for a slug.
 */
function looksLikeIdentifier(segment: string): boolean {
  if (!segment) return false;

  // UUID. Tested first: it would pass the slug rule below.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    return true;
  }

  /*
   * A human-written slug: lower case words joined by hyphens, such as
   * "staff-audit", "ownly-digital-limited", "project-quote-band". These are kept,
   * because they are exactly what makes the analytics worth having.
   *
   * The length check inside stops a hyphenated TOKEN slipping through on the
   * strength of having a hyphen in it: no real word is twelve characters of
   * unbroken gibberish.
   */
  if (/^[a-z0-9]+(-[a-z0-9]+)+$/.test(segment)) {
    const longestRun = Math.max(...segment.split("-").map((part) => part.length));
    if (longestRun < 12) return false;
    return true;
  }

  // Mixed case with length: the signature of a generated token.
  if (segment.length >= 12 && /[a-z]/.test(segment) && /[A-Z0-9]/.test(segment)) {
    return true;
  }

  // Long and unbroken: cuids such as cmogh66j8rcj8m0d2jdlclacn.
  if (segment.length >= 20) return true;

  return false;
}

/**
 * Replace anything identifying, keeping the page shape so the numbers stay useful.
 *
 * `/leads/cmogh66j8rcj8m0d2jdlclacn` becomes `/leads/[id]`: you still learn that
 * somebody opened a lead, just not which one.
 */
export function redactPath(pathname: string): string {
  // Explicit credential routes first, redacted whatever they contain, including
  // a token short enough for the shape test to miss.
  for (const route of CREDENTIAL_ROUTES) {
    const prefix = `/${route}/`;
    if (pathname.startsWith(prefix)) return `${prefix}[redacted]`;
  }

  // Known record routes next, for the same reason: a short id would otherwise
  // survive the shape test below.
  for (const route of RECORD_ROUTES) {
    const prefix = `/${route}/`;
    if (pathname.startsWith(prefix)) return `${prefix}[id]`;
  }

  // Then everything else, segment by segment, redact by default. This is what
  // covers the routes that do not exist yet.
  return pathname
    .split("/")
    .map((segment) => (looksLikeIdentifier(segment) ? "[id]" : segment))
    .join("/");
}

export function RedactedAnalytics() {
  return (
    <Analytics
      beforeSend={(event) => {
        try {
          const url = new URL(event.url);
          url.pathname = redactPath(url.pathname);
          /*
           * Query strings go entirely. Nothing here needs them, and they are
           * where a token ends up the day somebody switches a link from a path
           * segment to `?token=`, which would otherwise silently reopen this.
           */
          url.search = "";
          url.hash = "";
          return { ...event, url: url.toString() };
        } catch {
          /* Unparseable URL: drop the event rather than guess what is in it. */
          return null;
        }
      }}
    />
  );
}
